import { useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ErrorIcon, ExpandLessIcon, ExpandMoreIcon } from '../../assets';

export function StatusHistorySection({
  note,
  children,
}: {
  note: React.ReactNode;
  children: React.ReactNode;
}) {
  const { classes } = useStyles();
  const [isOpen, setIsOpen] = useState(true);

  return (
    <section className={classes.root}>
      <div className={classes.header}>
        <h1 className={classes.title}>Status history</h1>
        <IconButton
          onClick={() => setIsOpen((v) => !v)}
          size='small'
          aria-expanded={isOpen}
          aria-label={
            isOpen ? 'Collapse status history' : 'Expand status history'
          }
        >
          {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </div>
      <div style={{ display: isOpen ? 'block' : 'none' }}>
        <Box className={classes.note}>
          <ErrorIcon className={classes.noteIcon} />
          <Typography className={classes.noteText}>{note}</Typography>
        </Box>
        {children}
      </div>
    </section>
  );
}

const useStyles = makeStyles({ name: { StatusHistorySection } })((theme) => ({
  // Sections above already leave a 24px gap
  root: {},
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontFamily: 'Montserrat',
    fontSize: 19,
    fontWeight: 700,
    letterSpacing: 0,
    margin: 0,
  },
  note: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    maxWidth: 720,
    margin: '8px 0 12px',
  },
  noteIcon: {
    flex: 'none',
    fontSize: 16,
    marginTop: 2,
    color: '#F5A623',
  },
  noteText: {
    fontSize: 12,
    lineHeight: 1.55,
    color: theme.palette.text.secondary,
  },
}));

/** Explains a status row using only what the timeline records. */
export function getStatusChangeNote({
  entity,
  statusUpdateDisplay = '',
  hasOldStatus,
  durationDisplay,
  seconds,
  changedBy,
  isActivity,
}: {
  entity: string;
  statusUpdateDisplay?: string;
  hasOldStatus: boolean;
  durationDisplay?: string;
  seconds: number;
  changedBy?: string | null;
  isActivity?: boolean;
}) {
  if (!hasOldStatus) {
    return `The ${entity} started in ${statusUpdateDisplay}${
      changedBy ? `, set by ${changedBy}` : ''
    }.`;
  }
  const [from, to] = statusUpdateDisplay.split(' - ');
  const moved =
    seconds === 0
      ? `The ${entity} moved from ${from} to ${to} with no time in ${from}.`
      : `The ${entity} was in ${from} for ${durationDisplay} before ${
          changedBy ? `${changedBy} moved it` : 'it moved'
        } to ${to}.`;
  return isActivity
    ? `${moved} Employee work during this time is logged separately in the Employee activity log.`
    : moved;
}

/** Total seconds from the days/hours/minutes/seconds parts the timeline returns. */
export function getStatusRowSeconds(row: {
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
}) {
  return (
    (row.days ?? 0) * 86400 +
    (row.hours ?? 0) * 3600 +
    (row.minutes ?? 0) * 60 +
    (row.seconds ?? 0)
  );
}
